import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import JsonLd from '../../components/JsonLd/JsonLd';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import CoberturaNota from '../../components/CoberturaNota/CoberturaNota';
import './IaEnProduccionPage.css';

const PAGE_URL = 'https://e-asy.es/servicios/ia-en-produccion';

const FAQ_ITEMS = [
  {
    question: '¿Hace falta que seamos una empresa de software?',
    answer:
      'No. El caso típico es una pyme de servicios, un despacho o un comercio que ha montado un prototipo con ChatGPT, Cursor, un freelance o una herramienta no-code y no sabe cómo ponerlo a funcionar de verdad, con usuarios, datos y un sitio seguro donde vive.',
  },
  {
    question: '¿Vosotros hacéis también la pantalla que ve el cliente?',
    answer:
      'Podemos, pero el modelo más habitual es el contrario: vosotros (o vuestro freelance) mantenéis el frontend y la lógica de negocio; nosotros el backend, la API, el acceso de usuarios, la conexión con otras herramientas y el despliegue en un entorno seguro (Azure).',
  },
  {
    question: '¿Qué diferencia hay entre la auditoría y la implementación?',
    answer:
      'La auditoría es el gancho de entrada: revisamos riesgos de seguridad y escalabilidad y os dejamos un informe con recomendación. La implementación es un proyecto a precio cerrado: construimos y desplegamos. El mantenimiento es una cuota mensual (hosting, soporte y monitorización).',
  },
  {
    question: '¿Y si hay datos de clientes, salud o menores?',
    answer:
      'Entonces no se trata solo de “subirlo a la nube”. Coordinamos con Ciberseguridad y cumplimiento y, si el dato no puede salir a una API pública, con IA privada / soberana.',
  },
  {
    question: '¿Podéis desplegar el agente o la app de IA que ya tenemos?',
    answer:
      'Sí. Es el caso habitual: la demo funciona y en producción no hay backend, usuarios ni un sitio donde viva. Revisamos lo que hay, montamos el suelo (API, autenticación, Azure) y lo operamos. No hace falta tirarlo y empezar de cero.',
  },
  {
    question: '¿Es lo mismo que un chatbot o que Agentes IA?',
    answer:
      'No. Agentes IA es diseñar e integrar el agente con Vulpin, Business Central o el CRM. Esta ficha es desplegar lo que ya habéis construido (vosotros, un freelance o una herramienta) para que aguante el día a día. Se pueden contratar las dos.',
  },
  {
    question: '¿El despliegue va siempre en Azure?',
    answer:
      'Azure es el entorno por defecto. Si el dato no puede salir a una API pública, el despliegue es on-premise o nube privada: eso está en IA privada. Lo decimos en la auditoría, no al final del proyecto.',
  },
];

const IaEnProduccionPage = ({ darkMode, toggleDarkMode }) => {
  const paquetes = [
    {
      numero: '01',
      etiqueta: 'Gancho de entrada',
      titulo: 'Auditoría de despliegue',
      descripcion:
        'Revisamos lo que ya tenéis (prototipo, agente, automatización) y os decimos, sin postureo, si se puede llevar a producción, qué riesgos hay y qué haría falta.',
      caracteristicas: [
        'Revisión de seguridad: claves, datos, quién puede entrar',
        'Escalabilidad: qué pasa cuando lo usen 10 personas, no una',
        'Huecos de backend, autenticación e integraciones',
        'Informe con recomendación: seguir, rehacer o parar',
      ],
      nota: 'Punto de partida comercial. Sin compromiso de implementación.',
    },
    {
      numero: '02',
      etiqueta: 'Precio cerrado por proyecto',
      titulo: 'Implementación',
      descripcion:
        'Construimos el backend y lo ponemos en un entorno seguro. Vosotros seguís con el frontend y la lógica de negocio si así lo queréis.',
      caracteristicas: [
        'Backend y API para que el prototipo deje de vivir en un portátil',
        'Autenticación de usuarios y control de accesos',
        'Integración de herramientas y APIs (CRM, WhatsApp, ERP, pasarelas…)',
        'Despliegue en Azure, en un entorno que se puede operar y auditar',
      ],
      nota: 'Alcance y precio cerrados antes de empezar. Sin “ya veremos las horas”.',
    },
    {
      numero: '03',
      etiqueta: 'Retainer mensual',
      titulo: 'Mantenimiento',
      descripcion:
        'Que no se caiga el viernes. Hosting, soporte y monitorización para que el agente o la automatización sigan en marcha cuando el prototipo ya es un servicio.',
      caracteristicas: [
        'Hosting en el entorno acordado',
        'Soporte cuando algo falla o hay que tocar una integración',
        'Monitorización: enterarnos antes que el cliente',
        'Actualizaciones de seguridad del backend y el despliegue',
      ],
      nota: 'Cuota mensual. Ingreso recurrente, operación previsible para vosotros.',
    },
  ];

  const casos = [
    {
      titulo: 'Despacho que redacta con IA',
      descripcion:
        'Una gestoría o un despacho ha montado, con ChatGPT o Cursor, un asistente que borra borradores de escritos. En el portátil de una persona funciona. En producción no: no hay login, los documentos salen hacia una API pública y nadie sabe dónde se guardan.',
      puntos: [
        'Ellos siguen afinando el tono y las plantillas (su lógica).',
        'Nosotros: usuarios, API, conexión al archivo de expedientes y despliegue en Azure.',
        'Si hay datos de clientes sensibles, entra Ciberseguridad en el mismo criterio.',
      ],
    },
    {
      titulo: 'Empresa de reformas con un “presupuestador”',
      descripcion:
        'Una empresa de servicios (reformas, instalaciones, mantenimiento) ha hecho con no-code o un freelance una herramienta que estima partidas. Quiere que comerciales y clientes la usen de verdad, no en una demo.',
      puntos: [
        'Ellos mantienen la pantalla y las reglas de precio.',
        'Nosotros el backend, el acceso por usuario y la integración con su facturación o WhatsApp.',
        'Mantenimiento mensual para que no se rompa cada vez que cambia una API.',
      ],
    },
    {
      titulo: 'Clínica con un bot de citas',
      descripcion:
        'Una clínica o centro de fisioterapia ha conectado un agente de WhatsApp con una herramienta no-code. Contesta bien… hasta que pide un historial o un dato de salud. Ahí el prototipo se convierte en un problema de cumplimiento.',
      puntos: [
        'Ellos definen qué debe preguntar el bot (su operativa).',
        'Nosotros el backend seguro, la autenticación y el entorno de despliegue.',
        'Ciberseguridad y, si el dato no puede ir a la nube pública, IA privada.',
      ],
    },
    {
      titulo: 'Hotel o restaurante con un FAQ inteligente',
      descripcion:
        'Han entrenado un chat con las preguntas de siempre (horarios, menú, check-in). Lo quieren en la web y conectado a reservas, no como una ventana suelta de ChatGPT.',
      puntos: [
        'Ellos actualizan contenidos y el criterio de respuestas.',
        'Nosotros API, conexión al sistema de reservas y puesta en producción.',
        'Monitorización para que el chat no “se caiga” en temporada alta.',
      ],
    },
  ];

  const beneficios = [
    {
      titulo: 'De demo a servicio',
      descripcion:
        'El prototipo deja de vivir en un hilo de ChatGPT o en el PC de quien lo montó. Tiene sitio, usuarios y un responsable de que arranque.',
    },
    {
      titulo: 'Sin dejar las llaves en la puerta',
      descripcion:
        'Claves, accesos y datos fuera del prototipo. El backend no es un extra: es lo que evita que un agente se convierta en una fuga.',
    },
    {
      titulo: 'Conectado a lo que ya usáis',
      descripcion:
        'CRM, WhatsApp, ERP, pasarela, calendario. La integración de herramientas y APIs entra aquí, no como categoría suelta.',
    },
    {
      titulo: 'Vosotros el frente, nosotros el suelo',
      descripcion:
        'Si queréis hacer el frontend o ya tenéis freelance, encaja. Nosotros backend, autenticación y despliegue.',
    },
    {
      titulo: 'Entorno que se puede operar',
      descripcion:
        'Azure (u otro entorno seguro acordado): no un “lo subí yo un domingo” que nadie puede tocar a los seis meses.',
    },
    {
      titulo: 'Después del go-live',
      descripcion:
        'El retainer de mantenimiento es para que el viernes no os llamen porque el bot ha dejado de responder.',
    },
  ];

  const proceso = [
    {
      paso: '1',
      titulo: 'Auditoría de despliegue',
      descripcion:
        'Miramos el prototipo, los datos que toca y las herramientas que queréis conectar. Salís con un informe y una recomendación clara.',
    },
    {
      paso: '2',
      titulo: 'Alcance cerrado',
      descripcion:
        'Si encaja implementar, cerramos qué va en el backend, qué queda en vuestro lado y el precio del proyecto. Sin alcance infinito.',
    },
    {
      paso: '3',
      titulo: 'Backend, APIs y Azure',
      descripcion:
        'Construimos, integramos y desplegamos. Vosotros podéis seguir iterando la interfaz mientras el suelo está estable.',
    },
    {
      paso: '4',
      titulo: 'Puesta en producción',
      descripcion:
        'Usuarios reales, monitorización y un entorno que se puede enseñar a dirección o a un auditor si hace falta.',
    },
    {
      paso: '5',
      titulo: 'Mantenimiento',
      descripcion:
        'Hosting, soporte y vigilancia. La IA deja de ser un experimento y pasa a ser un servicio que alguien opera.',
    },
  ];

  const faqSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      url: PAGE_URL,
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    }),
    []
  );

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Desplegar aplicaciones de IA en producción (Azure)',
    description:
      'Desplegamos en producción prototipos y agentes de IA: backend, API, autenticación e infraestructura en Azure, con auditoría previa y mantenimiento. Para pymes que ya tienen una demo y no un producto.',
    url: PAGE_URL,
    serviceType: 'Despliegue de IA en producción',
    provider: {
      '@type': 'Organization',
      name: 'E-ASY',
      url: 'https://e-asy.es',
    },
    areaServed: [
      { '@type': 'AdministrativeArea', name: 'Canarias' },
      { '@type': 'Country', name: 'España' },
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Paquetes de IA en producción',
      itemListElement: [
        {
          '@type': 'Offer',
          name: 'Auditoría de despliegue',
          description:
            'Revisión de riesgos de seguridad y escalabilidad e informe con recomendación.',
        },
        {
          '@type': 'Offer',
          name: 'Implementación',
          description:
            'Backend, API, autenticación, integración de herramientas y despliegue en Azure. Precio cerrado por proyecto.',
        },
        {
          '@type': 'Offer',
          name: 'Mantenimiento',
          description:
            'Hosting, soporte y monitorización en cuota mensual.',
        },
      ],
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://e-asy.es/' },
      { '@type': 'ListItem', position: 2, name: 'Servicios', item: 'https://e-asy.es/servicios' },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'IA en producción',
        item: PAGE_URL,
      },
    ],
  };

  return (
    <>
      <SEO
        title="Desplegar IA en producción en Azure | E-ASY"
        description="Despliega tu aplicación o agente de IA: del prototipo (ChatGPT, Cursor, no-code) a backend, API y Azure, con mantenimiento. Proyectos en remoto para toda España, con sede en Tenerife."
        keywords="desplegar IA en producción, desplegar agente IA Azure, llevar prototipo IA a producción, backend para ChatGPT, API agente IA, Cursor producción, no-code IA producción, E-ASY Tenerife"
        canonical={PAGE_URL}
      />
      <JsonLd id="jsonld-iaprod-faq" data={faqSchema} />
      <JsonLd id="jsonld-iaprod-service" data={serviceSchema} />
      <JsonLd id="jsonld-iaprod-breadcrumb" data={breadcrumbSchema} />
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      <div className={`ia-en-produccion-page ${darkMode ? 'dark' : 'light'}`}>
        <section className="ia-prod-hero">
          <div className="ia-prod-hero-content">
            <div className="ia-prod-hero-text">
              <h1 className="ia-prod-hero-title">
                Despliega tu aplicación de IA en producción
              </h1>
              <p className="ia-prod-hero-subtitle">
                No hace falta ser una empresa de software. El caso de todos los días:
                una <strong>empresa de servicios</strong> (reformas, clínica, despacho,
                hotel) ha montado un agente o una automatización con{' '}
                <strong>ChatGPT, Cursor, un freelance o una herramienta no-code</strong>
                . En la demo funciona. En el día a día, no hay backend, no hay un sitio
                seguro donde vivirlo y nadie quiere ser “el informático” cuando se cae.
              </p>
              <p className="ia-prod-hero-subtitle">
                Vosotros podéis seguir con el frontend y la lógica. Nosotros el{' '}
                <strong>backend</strong>, las <strong>APIs</strong>, el acceso de
                usuarios y el <strong>despliegue en Azure</strong>. Tres paquetes:
                auditoría, implementación a precio cerrado y mantenimiento mensual.
              </p>
              <div className="ia-prod-hero-cta">
                <Link to="/contacto" className="ia-prod-hero-button primary">
                  Agenda una consultoría gratuita
                </Link>
                <Link to="/servicios" className="ia-prod-hero-button secondary">
                  Ver todos los servicios
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="ia-prod-que-es">
          <div className="ia-prod-que-es-content">
            <div className="ia-prod-que-es-header">
              <h2 className="ia-prod-que-es-title">
                El prototipo se queda atascado antes de producción
              </h2>
              <p className="ia-prod-que-es-subtitle">
                Montar un agente o una automatización con IA es cada vez más fácil.
                Ponerlo a trabajar con clientes reales es otra cosa: no hay backend,
                las claves están en un archivo, el dato sale a una API pública y no
                existe un entorno donde desplegar sin que se caiga a la primera.
              </p>
              <p className="ia-prod-que-es-subtitle">
                Muchas pymes no quieren (ni deben) convertirse en un equipo de
                desarrollo. Quieren que alguien especializado haga el suelo —backend,
                autenticación, integración de herramientas y despliegue— y que ellos
                sigan con lo que sí dominan: el servicio y, si les encaja, la
                pantalla.
              </p>
            </div>
          </div>
        </section>

        <section className="ia-prod-beneficios">
          <div className="ia-prod-beneficios-content">
            <div className="ia-prod-beneficios-header">
              <h2 className="ia-prod-beneficios-title">Para qué sirve este pilar</h2>
              <p className="ia-prod-beneficios-subtitle">
                No es “haceros una IA” ni el mismo servicio que{' '}
                <Link to="/agentes-ia">Agentes IA</Link>
                {' '}(diseñar el bot e integrarlo). Aquí desplegamos lo que ya
                habéis empezado para que se pueda operar, con mantenimiento
                detrás.
              </p>
            </div>
            <div className="ia-prod-beneficios-grid">
              {beneficios.map((item) => (
                <div key={item.titulo} className="ia-prod-beneficio-card">
                  <h3 className="beneficio-title">{item.titulo}</h3>
                  <p className="beneficio-description">{item.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="ia-prod-servicios">
          <div className="ia-prod-servicios-content">
            <div className="ia-prod-servicios-header">
              <h2 className="ia-prod-servicios-title">Tres paquetes</h2>
              <p className="ia-prod-servicios-subtitle">
                Entrada con auditoría, proyecto cerrado e ingreso recurrente de
                mantenimiento. Podéis empezar por el 01.
              </p>
            </div>
            <div className="ia-prod-servicios-grid ia-prod-paquetes-grid">
              {paquetes.map((paquete) => (
                <article key={paquete.numero} className="ia-prod-servicio-card ia-prod-paquete-card">
                  <div className="ia-prod-paquete-top">
                    <span className="ia-prod-paquete-num">{paquete.numero}</span>
                    <span className="ia-prod-paquete-tag">{paquete.etiqueta}</span>
                  </div>
                  <h3 className="servicio-title">{paquete.titulo}</h3>
                  <p className="servicio-description">{paquete.descripcion}</p>
                  <div className="servicio-casos">
                    <h4 className="casos-title">Incluye:</h4>
                    <ul className="casos-list">
                      {paquete.caracteristicas.map((linea) => (
                        <li key={linea} className="caso-item">
                          {linea}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="ia-prod-paquete-nota">{paquete.nota}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="ia-prod-sectores">
          <div className="ia-prod-sectores-content">
            <div className="ia-prod-sectores-header">
              <h2 className="ia-prod-sectores-title">Casos de uso reconocibles</h2>
              <p className="ia-prod-sectores-subtitle">
                El cliente pone el oficio y, si quiere, la pantalla. Nosotros el
                backend y el despliegue.
              </p>
            </div>
            <div className="ia-prod-sectores-grid ia-prod-casos-grid">
              {casos.map((caso) => (
                <article key={caso.titulo} className="ia-prod-sector-card">
                  <h3 className="sector-title">{caso.titulo}</h3>
                  <p className="sector-description">{caso.descripcion}</p>
                  <h4 className="casos-title">Cómo se parte el trabajo</h4>
                  <ul className="casos-list">
                    {caso.puntos.map((punto) => (
                      <li key={punto} className="caso-item">
                        {punto}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
            <p className="ia-prod-ciber-callout">
              Si el caso toca <strong>datos sensibles</strong> (salud, menores,
              dependencia, clientes con categoría especial) o necesitáis que el
              modelo <strong>no salga a la nube pública</strong>, no lo resolvemos
              solo con un despliegue en Azure: entra{' '}
              <Link to="/servicios/ciberseguridad-cumplimiento">
                Ciberseguridad y cumplimiento
              </Link>
              . Si el expediente ya vive (o debería vivir) en nuestro ERP, partimos de{' '}
              <Link to="/vulpin">Vulpin Suite</Link>
              {' '}y, cuando toca, de{' '}
              <Link to="/ia-privada">IA privada / soberana</Link>
              . Si aún no hay prototipo y hay que diseñar el agente sobre el ERP, esa
              ficha es{' '}
              <Link to="/agentes-ia">Agentes IA</Link>
              . El suelo de suscripción, redes y costes en Azure está en{' '}
              <Link to="/servicios/azure">consultoría Azure</Link>.
            </p>
          </div>
        </section>

        <section className="ia-prod-proceso">
          <div className="ia-prod-proceso-content">
            <div className="ia-prod-proceso-header">
              <h2 className="ia-prod-proceso-title">Cómo lo encajamos</h2>
              <p className="ia-prod-proceso-subtitle">
                Auditoría primero. Implementación solo si el informe dice que merece
                la pena. Mantenimiento para que no vuelva a ser un prototipo.
              </p>
            </div>
            <div className="ia-prod-proceso-timeline">
              {proceso.map((item) => (
                <div key={item.paso} className="proceso-step">
                  <div className="step-number">{item.paso}</div>
                  <div className="step-content">
                    <h3 className="step-title">{item.titulo}</h3>
                    <p className="step-description">{item.descripcion}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="ia-prod-faq">
          <div className="ia-prod-faq-content">
            <div className="ia-prod-faq-header">
              <h2 className="ia-prod-faq-title">Preguntas frecuentes</h2>
            </div>
            <div className="ia-prod-faq-list">
              {FAQ_ITEMS.map((item) => (
                <details key={item.question} className="ia-prod-faq-item">
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
            <p className="ia-prod-faq-note">
              Lecturas:{' '}
              <Link to="/blog/de-prototipo-chatgpt-cursor-a-produccion">
                de prototipo ChatGPT o Cursor a producción
              </Link>
              {' · '}
              <Link to="/blog/donde-desplegar-agente-ia-azure-on-premise">
                dónde desplegar un agente (Azure, on-premise o API)
              </Link>
              .
            </p>
          </div>
        </section>

        <section className="ia-prod-cta">
          <div className="ia-prod-cta-content">
            <h2 className="ia-prod-cta-title">
              ¿El prototipo ya no cabe en una demo?
            </h2>
            <p className="ia-prod-cta-subtitle">
              Revisamos qué tenéis, qué falta para producción y si el siguiente paso
              es una auditoría, un proyecto cerrado o no tocarlo todavía.
            </p>
            <div className="ia-prod-cta-buttons">
              <Link to="/contacto" className="ia-prod-cta-button primary">
                Agenda una consultoría gratuita
              </Link>
              <Link to="/servicios" className="ia-prod-cta-button secondary">
                Ver todos los servicios
              </Link>
            </div>
          </div>
        </section>
      </div>
      <CoberturaNota />
      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default IaEnProduccionPage;
